import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Building2, MapPin, ExternalLink } from "lucide-react";
import { useAuth } from "../../auth/useAuth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { listAllDealsBasic } from "@/lib/data/deals";

const intro = `Here we distill our experience into a standard process that takes you from an initial idea to a successfully operating hotel. We'll keep uploading templates, adding new steps, and improving guidance over time. Start by linking a project you've created in Underwriting, or explore the roadmap without tying it to a deal.`;

export default function UserRoadmapLanding() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [deals, setDeals] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>("");

  useEffect(() => {
    async function loadData() {
      try {
        const data = await listAllDealsBasic();
        setDeals(data);
      } catch (err) {
        console.error("Error loading deals:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const actualDeals = deals || [];
  const hasDeals = actualDeals.length > 0;

  const options = useMemo(
    () =>
      hasDeals
        ? actualDeals.map((d) => ({
            id: d.id,
            displayName: d.name || `Deal ID: ${d.id}`,
          }))
        : [],
    [hasDeals, actualDeals],
  );

  const currentDealName = useMemo(() => {
    if (!selectedId || !hasDeals) return null;
    const selectedOption = options.find((o) => o.id === selectedId);
    return selectedOption ? selectedOption.displayName : null;
  }, [selectedId, hasDeals, options]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 max-w-4xl py-8">
      <div className="text-center mb-12">
        <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
          {intro}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
          <CardHeader>
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Link a Project
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasDeals ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Choose your project
                  </label>

                  <Select
                    value={currentDealName}
                    onValueChange={(value) => setSelectedId(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a project…" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* 
                {selectedId && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    {(() => {
                      const selectedDeal = actualDeals.find(
                        (d) => d.id === selectedId
                      );
                      if (!selectedDeal) return null;

                      return (
                        <div className="flex items-center space-x-2 text-sm text-slate-600">
                          <MapPin className="h-4 w-4" />
                          <span>{selectedDeal.location}</span>
                          <span>•</span>
                          <span>
                            {selectedDeal.roomTypes?.reduce(
                              (sum, rt) => sum + rt.rooms,
                              0
                            ) || 0}{" "}
                            rooms
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )} */}

                <Button
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                  disabled={!selectedId}
                  onClick={() => nav(`/roadmap/${selectedId}`)}
                >
                  Open Roadmap for this Project
                </Button>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-slate-600 mb-4">
                  You don't have any projects yet. Create one in{" "}
                  <span className="font-semibold text-brand-600">
                    Underwriting
                  </span>
                  , then return here to link it.
                </p>
                {user && (
                  <Button
                    variant="outline"
                    onClick={() => nav("/underwriting")}
                    className="flex items-center space-x-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Go to Underwriting</span>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-xl">Just Explore</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Browse the roadmap structure and example steps without connecting
              to a specific project. Perfect for understanding the hotel
              development process.
            </p>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs text-slate-500 space-y-1">
                <div>• View all development phases</div>
                <div>• See example timelines and milestones</div>
                <div>• Understand the hotel opening process</div>
              </div>
            </div>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => nav("/roadmap/explore")}
            >
              Explore Without Project
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="mt-12 text-center">
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            What's in the Roadmap?
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-slate-600">
            <div>
              <div className="font-medium text-slate-900 mb-1">
                6 Development Phases
              </div>
              <div>
                From ideation to pre-opening, covering every major milestone
              </div>
            </div>
            <div>
              <div className="font-medium text-slate-900 mb-1">
                Detailed Steps
              </div>
              <div>
                Specific tasks, timelines, and deliverables for each phase
              </div>
            </div>
            <div>
              <div className="font-medium text-slate-900 mb-1">
                Expert Guidance
              </div>
              <div>
                Best practices from successful hotel development projects
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
